import { useRef, useState } from "react";
import { parseGeoJSON, parseKML, recordsToParquet } from "../utils/geoToParquet";

const PORTAL_API_URL = process.env.REACT_APP_API_URL || "";
const MAX_BYTES      = 150 * 1024 * 1024;

const PHASE = {
  idle:       "idle",
  parsing:    "parsing",    // reading + parsing file
  converting: "converting", // DuckDB → Parquet (point-only path)
  uploading:  "uploading",  // PUT to R2
  done:       "done",
  error:      "error",
};

export function ClientLayerUpload({ db, projectId, getToken, onUploaded, onClose }) {
  const [phase,    setPhase]    = useState(PHASE.idle);
  const [progress, setProgress] = useState(0); // 0-100
  const [message,  setMessage]  = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["geojson", "kml"].includes(ext)) {
      setPhase(PHASE.error);
      setMessage("Only .geojson and .kml files are supported.");
      return;
    }

    try {
      // ── 1. Parse ──────────────────────────────────────────────────────────
      setPhase(PHASE.parsing);
      setProgress(10);
      setMessage(`Reading ${file.name}…`);

      const text = await file.text();
      const { records, skipped, hasNonPoint, geojsonCollection } =
        ext === "geojson" ? parseGeoJSON(text) : parseKML(text);

      if (hasNonPoint) {
        await uploadGeoJSON({ file, geojsonCollection, records, skipped });
      } else {
        if (!db) {
          setPhase(PHASE.error);
          setMessage("Globe is still loading — please wait a moment and try again.");
          return;
        }
        await uploadParquet({ file, records, skipped });
      }

    } catch (err) {
      setPhase(PHASE.error);
      setMessage(err.message || "Upload failed");
    }
  }

  // ── GeoJSON path (polygon / line / mixed files) ──────────────────────────

  async function uploadGeoJSON({ file, geojsonCollection, records, skipped }) {
    const featureCount = geojsonCollection.features.length;
    const skipNote     = skipped ? ` (${skipped} unparseable feature${skipped > 1 ? "s" : ""} skipped)` : "";
    setMessage(`Parsed ${featureCount} feature${featureCount !== 1 ? "s" : ""}${skipNote} — preparing upload…`);
    setProgress(35);

    const geojsonStr   = JSON.stringify(geojsonCollection);
    const geojsonBytes = new TextEncoder().encode(geojsonStr);

    if (geojsonBytes.byteLength > MAX_BYTES) {
      const mb = (geojsonBytes.byteLength / 1024 / 1024).toFixed(1);
      throw new Error(`File is ${mb} MB after parsing — exceeds the 150 MB per-layer limit.`);
    }

    setProgress(50);
    setMessage("Requesting upload slot…");
    setPhase(PHASE.uploading);

    const baseName    = file.name.replace(/\.(geojson|kml)$/i, "");
    const displayName = baseName;
    const filename    = `${baseName}.geojson`;

    const token      = await getToken();
    const presignRes = await fetch(`${PORTAL_API_URL}/api/client-layers?presign=1`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({
        project_id: projectId,
        filename,
        size_bytes: geojsonBytes.byteLength,
        name:       displayName,
        file_type:  "geojson",
      }),
    });

    if (!presignRes.ok) {
      const err = await presignRes.json().catch(() => ({ error: presignRes.statusText }));
      throw new Error(err.error || "Failed to get upload slot");
    }
    const { presignedUrl, id, key, name } = await presignRes.json();

    setProgress(65);
    setMessage("Uploading to storage…");

    const putRes = await fetch(presignedUrl, {
      method:  "PUT",
      headers: { "Content-Type": "application/geo+json" },
      body:    geojsonBytes,
    });
    if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

    setProgress(90);
    setMessage("Registering layer…");

    await fetch(`${PORTAL_API_URL}/api/client-layers?register=1`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({ id, size_bytes: geojsonBytes.byteLength }),
    });

    setProgress(100);
    setPhase(PHASE.done);
    setMessage(`"${name}" added — ${featureCount} feature${featureCount !== 1 ? "s" : ""}, ${(geojsonBytes.byteLength / 1024 / 1024).toFixed(2)} MB`);
    onUploaded({ id, key, name, size_bytes: geojsonBytes.byteLength, file_type: "geojson" });
  }

  // ── Parquet path (point-only files) ─────────────────────────────────────

  async function uploadParquet({ file, records, skipped }) {
    const skipNote = skipped ? ` (${skipped} non-point feature${skipped > 1 ? "s" : ""} skipped)` : "";
    setMessage(`Parsed ${records.length.toLocaleString()} points${skipNote} — converting…`);
    setPhase(PHASE.converting);
    setProgress(35);

    const parquetBytes = await recordsToParquet(db, records);
    const sizeMB = (parquetBytes.byteLength / 1024 / 1024).toFixed(2);

    if (parquetBytes.byteLength > MAX_BYTES) {
      throw new Error(`Converted file is ${sizeMB} MB — exceeds the 150 MB per-layer limit. Reduce the number of features and try again.`);
    }

    setProgress(55);
    setMessage(`Converted to ${sizeMB} MB Parquet — requesting upload slot…`);
    setPhase(PHASE.uploading);

    const baseName    = file.name.replace(/\.(geojson|kml)$/i, "");
    const displayName = baseName;
    const filename    = `${baseName}.parquet`;

    const token      = await getToken();
    const presignRes = await fetch(`${PORTAL_API_URL}/api/client-layers?presign=1`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({
        project_id: projectId,
        filename,
        size_bytes: parquetBytes.byteLength,
        name:       displayName,
        file_type:  "parquet",
      }),
    });

    if (!presignRes.ok) {
      const err = await presignRes.json().catch(() => ({ error: presignRes.statusText }));
      throw new Error(err.error || "Failed to get upload slot");
    }
    const { presignedUrl, id, key, name } = await presignRes.json();

    setProgress(65);
    setMessage("Uploading to storage…");

    const putRes = await fetch(presignedUrl, {
      method:  "PUT",
      headers: { "Content-Type": "application/octet-stream" },
      body:    parquetBytes,
    });
    if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

    setProgress(90);
    setMessage("Registering layer…");

    await fetch(`${PORTAL_API_URL}/api/client-layers?register=1`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({ id, size_bytes: parquetBytes.byteLength }),
    });

    setProgress(100);
    setPhase(PHASE.done);
    setMessage(`"${name}" added — ${records.length.toLocaleString()} points, ${sizeMB} MB`);
    onUploaded({ id, key, name, size_bytes: parquetBytes.byteLength, file_type: "parquet" });
  }

  // ── Drag-and-drop handlers ────────────────────────────────────────────────

  function onDragOver(e)  { e.preventDefault(); setDragging(true);  }
  function onDragLeave(e) { e.preventDefault(); setDragging(false); }
  function onDrop(e)      { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }

  const busy = phase === PHASE.parsing || phase === PHASE.converting || phase === PHASE.uploading;

  return (
    <div style={{ fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
      {/* ── Drop zone ── */}
      {(phase === PHASE.idle || phase === PHASE.error) && (
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          style={{
            border:       `2px dashed ${dragging ? "#6366f1" : "#d1d5db"}`,
            borderRadius: 8,
            padding:      "18px 12px",
            textAlign:    "center",
            cursor:       "pointer",
            background:   dragging ? "#f0f0ff" : "#fafafa",
            transition:   "all 0.15s",
          }}
        >
          <div style={{ fontSize: 22, marginBottom: 6 }}>📂</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            Drop a file or click to browse
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            GeoJSON · KML &nbsp;·&nbsp; points, polygons & lines &nbsp;·&nbsp; max 150 MB
          </div>
          {phase === PHASE.error && (
            <div style={{
              marginTop: 10, padding: "7px 10px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 6, fontSize: 11, color: "#dc2626", lineHeight: 1.4,
            }}>
              {message}
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".geojson,.kml"
        style={{ display: "none" }}
        onChange={e => handleFile(e.target.files[0])}
      />

      {/* ── Progress ── */}
      {busy && (
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>{message}</div>
          <div style={{ height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", background: "#6366f1", borderRadius: 2,
              width: `${progress}%`, transition: "width 0.4s ease",
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4, textAlign: "right" }}>
            {progress}%
          </div>
        </div>
      )}

      {/* ── Done ── */}
      {phase === PHASE.done && (
        <div style={{
          padding: "10px 12px",
          background: "#f0fdf4", border: "1px solid #bbf7d0",
          borderRadius: 8, fontSize: 11, color: "#15803d", lineHeight: 1.5,
        }}>
          ✓ {message}
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => { setPhase(PHASE.idle); setMessage(""); setProgress(0); }}
              style={{
                background: "none", border: "1px solid #86efac",
                borderRadius: 5, padding: "3px 10px",
                fontSize: 11, color: "#15803d", cursor: "pointer",
              }}
            >
              Upload another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
